import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { adminAPI } from '../services/api';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'resolved', label: 'Handled' },
];

const REASON_OPTIONS = [
  { value: '', label: 'All reasons' },
  { value: 'spam', label: 'Spam' },
  { value: 'inappropriate_content', label: 'Inappropriate content' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'misleading_or_fraudulent', label: 'Misleading or fraudulent' },
  { value: 'other', label: 'Other' },
];

const formatDateTime = (iso) => {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return String(iso);
  }
};

const truncate = (value, length = 180) => {
  if (!value) return '–';
  if (value.length <= length) return value;
  return `${value.slice(0, length)}...`;
};

const statusBadgeClass = (status) => {
  switch (status) {
    case 'resolved':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800';
    case 'dismissed':
      return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800';
    case 'reviewed':
      return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800';
    default:
      return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800';
  }
};

const humanizeStatus = (status) => {
  if (status === 'resolved') return 'Handled';
  if (!status) return '–';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const humanizeReason = (reason) => {
  if (!reason) return '–';
  return reason.replace(/_/g, ' ');
};

const AdminReportsPage = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-reports', { page, search, statusFilter, reasonFilter }],
    queryFn: async () => {
      const params = {
        page,
        page_size: PAGE_SIZE,
      };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (reasonFilter) params.reason = reasonFilter;
      const response = await adminAPI.getReports(params);
      return response.data;
    },
    keepPreviousData: true,
  });

  const markHandledMutation = useMutation({
    mutationFn: (reportId) => adminAPI.updateReport(reportId, { status: 'resolved' }),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-reports']);
      toast.success('Report marked as handled');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Failed to update report');
    },
  });

  const deleteReportMutation = useMutation({
    mutationFn: (reportId) => adminAPI.deleteReport(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-reports']);
      toast.success('Report deleted');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Failed to delete report');
    },
  });

  const items = data?.items || [];
  const pagination = data?.pagination || { page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 0 };
  const summary = data?.summary || { openCount: 0 };

  const canGoPrev = useMemo(() => pagination.page > 1, [pagination.page]);
  const canGoNext = useMemo(
    () => pagination.totalPages && pagination.page < pagination.totalPages,
    [pagination.page, pagination.totalPages]
  );

  const handleDelete = (report) => {
    const confirmed = window.confirm(
      `Delete report "${report.id}"? Use this only for fake or spam reports.`
    );
    if (!confirmed) return;
    deleteReportMutation.mutate(report.id);
  };

  return (
    <div className="space-y-6 text-gray-900 dark:text-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Reports</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Review submitted project reports and mark them as handled or remove fake reports.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Search
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Filter by project, reporter, details, or report id"
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Reason
          </label>
          <select
            value={reasonFilter}
            onChange={(e) => {
              setReasonFilter(e.target.value);
              setPage(1);
            }}
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {REASON_OPTIONS.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-col gap-2 text-sm text-gray-500 dark:text-gray-400 md:flex-row md:items-center md:justify-between">
        <div>
          Total reports: <span className="font-medium">{pagination.total}</span>
        </div>
        <div>
          Open reports: <span className="font-medium">{summary.openCount}</span>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500 dark:text-gray-400 text-sm">Loading reports...</div>
        </div>
      ) : isError ? (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Failed to load reports.
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {items.map((report) => {
              const isHandled = report.status === 'resolved';
              const isUpdating = markHandledMutation.isLoading && markHandledMutation.variables === report.id;
              const isDeleting = deleteReportMutation.isLoading && deleteReportMutation.variables === report.id;

              return (
                <div
                  key={report.id}
                  className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-sm"
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-3 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadgeClass(report.status)}`}
                        >
                          {humanizeStatus(report.status)}
                        </span>
                        <span className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-200">
                          {humanizeReason(report.reason)}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{report.id}</span>
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div>
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Project
                          </div>
                          <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                            {report.project?.name || 'Untitled project'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 break-all">
                            {report.project?.id || '–'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Reporter
                          </div>
                          <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                            {report.reporter?.username || '–'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {report.reporter?.email || '–'}
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Details
                        </div>
                        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-700 dark:text-gray-200">
                          {truncate(report.details, 320)}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                        <span>Created: {formatDateTime(report.createdAt)}</span>
                        <span>Updated: {formatDateTime(report.updatedAt)}</span>
                      </div>
                    </div>

                    <div className="flex shrink-0 flex-row gap-2 lg:flex-col">
                      <Link
                        to={`/projects/${report.project?.id}`}
                        className="inline-flex items-center justify-center rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        Open project
                      </Link>
                      <button
                        type="button"
                        disabled={isHandled || isUpdating || isDeleting}
                        onClick={() => markHandledMutation.mutate(report.id)}
                        className={`inline-flex items-center justify-center rounded border px-3 py-2 text-sm font-medium ${
                          isHandled || isUpdating || isDeleting
                            ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            : 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700'
                        }`}
                      >
                        {isHandled ? 'Handled' : isUpdating ? 'Updating...' : 'Mark handled'}
                      </button>
                      <button
                        type="button"
                        disabled={isUpdating || isDeleting}
                        onClick={() => handleDelete(report)}
                        className={`inline-flex items-center justify-center rounded border px-3 py-2 text-sm font-medium ${
                          isUpdating || isDeleting
                            ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            : 'border-red-300 text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/30'
                        }`}
                      >
                        {isDeleting ? 'Deleting...' : 'Delete report'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {items.length === 0 && (
              <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                No reports found.
              </div>
            )}
          </div>

          <div className="flex items-center justify-between mt-4 text-sm">
            <div className="text-gray-500 dark:text-gray-400">
              Page {pagination.page} of {pagination.totalPages || 1}
            </div>
            <div className="space-x-2">
              <button
                type="button"
                onClick={() => canGoPrev && setPage((p) => Math.max(1, p - 1))}
                disabled={!canGoPrev}
                className={`inline-flex items-center rounded border px-3 py-1 ${
                  canGoPrev
                    ? 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => canGoNext && setPage((p) => p + 1)}
                disabled={!canGoNext}
                className={`inline-flex items-center rounded border px-3 py-1 ${
                  canGoNext
                    ? 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminReportsPage;
